import type { ApiResponse, ProgramWithStats, ProgramStatus } from '@fluenceur/types';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';
import { shouldRetryDashboardQuery } from '@/libs/query-client';

interface FetchProgramsParams {
  status?: ProgramStatus;
}

const fetchPrograms = async (params?: FetchProgramsParams): Promise<ProgramWithStats[]> => {
  const queryParams = new URLSearchParams();
  if (params?.status) {
    queryParams.append('status', params.status);
  }

  const url = `/dashboard/programs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await api.get<ApiResponse<ProgramWithStats[]>>(url);

  if (response.data.success === false || !response.data.data) {
    throw new Error(response.data.message ?? 'Failed to fetch programs');
  }

  return response.data.data;
};

interface UseListProgramOptions {
  enabled?: boolean;
}

export const useListProgram = (
  params?: FetchProgramsParams,
  options?: UseListProgramOptions
): UseQueryResult<ProgramWithStats[], Error> => {
  return useQuery<ProgramWithStats[], Error>({
    queryKey: ['programs', { status: params?.status ?? 'all' }] as const,
    queryFn: () => fetchPrograms(params),
    retry: (failureCount, error) => shouldRetryDashboardQuery(failureCount, error, 2),
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    enabled: options?.enabled ?? true,
  });
};
